import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { BehaviorSubject, filter, from, of, switchMap, tap } from 'rxjs'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxButtonModule, FlxDocumentViewerModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  DocumentPreviewTemplateConfig,
  ProcessActionTriggerEvent,
  ResolvedAction,
  config,
  documentPreviewAnalytics,
  fetchDocument,
  fileSourceNeedsBlobFetch,
  getActionsByTemplateConfigAndTrigger$,
  getDocumentSrc$,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { getBoolExpressionSignal, getDynamicPropSignal, getThemeIcon } from '../helpers'

@Component({
  selector: 'flx-document-preview-wrapper',
  standalone: true,
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [
    FlxDocumentViewerModule,
    FlxLocalizePipe,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
    FlxButtonModule,
  ],
  templateUrl: './document-preview-wrapper.component.html',
  styleUrl: './document-preview-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxDocumentPreviewWrapperComponent implements OnInit, OnDestroy, AfterViewInit {
  #destroyRef = inject(DestroyRef)
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  #actionService = inject(FlxActionService)

  // actions kept in sync with the process-actions store so they arrive even when the node's
  // details resolve after this component mounts (mirrors the flx-image CLICK pattern).
  #scrollActions = signal<ResolvedAction[]>([])
  #scrollEndActions = signal<ResolvedAction[]>([])

  // DOCUMENT_PREVIEW component events. Getters: the config input is not set yet here, and the
  // blob is read when the event is sent. Only a blob the wrapper fetched gives a file type: a later
  // plain URL source keeps the old blob in `fileBlob`, so the current `dataType` decides.
  #analytics = documentPreviewAnalytics(
    () => this.templateConfig(),
    () => (this.dataType() === 'blob' ? this.fileBlob()?.type : undefined)
  )

  templateConfig = input.required<DocumentPreviewTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  fileSrc = computed(() => getDocumentSrc$(this.templateConfig()))
  icons = computed(() => ({
    document: getThemeIcon(ThemeComponent.DocumentPreview, 'documentIcon'),
    empty: getThemeIcon(ThemeComponent.DocumentPreview, 'emptyIcon'),
    error: getThemeIcon(ThemeComponent.DocumentPreview, 'errorIcon'),
    reload: getThemeIcon(ThemeComponent.DocumentPreview, 'reloadIcon'),
    share: getThemeIcon(ThemeComponent.DocumentPreview, 'shareIcon'),
    view: getThemeIcon(ThemeComponent.DocumentPreview, 'viewIcon'),
  }))

  fileBlob = signal<Blob | null>(null)
  fileUrl = signal<string | null>(null)
  loading = signal(false)
  error = signal(false)
  dataType = signal<'url' | 'blob'>(null)
  retry$ = new BehaviorSubject<void>(null)
  isOpened = computed(
    () =>
      (this.flowxProps().hasAccordion && this.flowxProps().defaultView === 'EXPANDED') ||
      !this.flowxProps().hasAccordion
  )
  config = config()

  // expressions
  isHidden: Signal<boolean>

  // dynamic props
  title: Signal<string>
  subtitle: Signal<string>

  readonly DISMISS_LABEL = '@@sys_close'

  constructor() {
    // TODO: maybe rethink this into computeds, at some point
    const effectRef = effect(
      () => {
        const fileSrc$ = this.fileSrc()
        this.retry$
          .asObservable()
          .pipe(
            switchMap(() => fileSrc$),
            filter(Boolean),
            tap(() => {
              this.loading.set(true)
              this.error.set(false)
              this.#analytics.loadStarted()
            }),
            switchMap((docSrc: string) => {
              // FlowX backend documents (document plugin, or any `/download` endpoint) need
              // the authenticated blob fetch and are same-origin, so no CORS concern. Every
              // other source (media library, static or variable external URLs) is a plain
              // URL handed directly to the viewer; fetching those as a blob via XHR would
              // trigger a CORS preflight that external hosts typically don't support.
              if (fileSourceNeedsBlobFetch(this.flowxProps()?.document, docSrc)) {
                return from(fetchDocument(docSrc))
              }
              return of(docSrc)
            }),
            takeUntilDestroyed(this.#destroyRef)
          )
          .subscribe((fileResource: Blob | string | null) => {
            this.loading.set(false)
            if (typeof fileResource === 'string') {
              this.dataType.set('url')
              this.fileUrl.set(fileResource)
            } else {
              this.dataType.set('blob')
              this.fileBlob.set(fileResource)
              if (!this.fileBlob()) {
                this.error.set(true)
              }
            }
          })
        // This makes sure that the effect runs only once. It is a bit defensive since fileSrc() (which is a computed property based on templateConfig signal)
        // should not change more than once because template config is (or should be) a fixed input, so we might not need to unsubscribe from this effect
        effectRef.destroy()
      },
      { manualCleanup: true }
    )
  }

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const { title, subtitle, hide } = this.flowxProps()
      this.title = getDynamicPropSignal(title, this.templateConfig())
      this.subtitle = getDynamicPropSignal(subtitle, this.templateConfig())
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
    })

    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.SCROLL)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#scrollActions.set(actions))

    getActionsByTemplateConfigAndTrigger$(
      this.templateConfig(),
      ProcessActionTriggerEvent.DOCUMENT_END
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#scrollEndActions.set(actions))
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
      // A failed blob fetch counts once the preview is on screen: a hidden preview renders nothing.
      effect(() => {
        if (!this.loading() && this.error() && !this.isHidden()) {
          untracked(() => this.#analytics.failed())
        }
      })
    })
  }

  ngOnDestroy(): void {
    this.retry$.complete()
  }

  // The viewer fetches a plain URL itself and reports it through its `loading` and `error` models.
  onViewerLoadingChange(loading: boolean): void {
    if (loading) {
      this.#analytics.loadStarted()
    }
  }

  onViewerError(error: boolean): void {
    if (error) {
      this.#analytics.failed()
    }
  }

  onAccordionOpenedChange(opened: boolean): void {
    if (opened) {
      this.#analytics.opened()
    } else {
      this.#analytics.closed()
    }
  }

  // The viewer fetches a plain URL again in each modal it opens, after this event: the open waits
  // for that load, not the result of the previous one.
  onModalOpened(): void {
    if (this.dataType() === 'url') {
      this.#analytics.loadStarted()
    }
    this.#analytics.opened()
  }

  onModalClosed(): void {
    this.#analytics.closed()
  }

  onRetried(): void {
    this.#analytics.retried()
  }

  onDocumentLoaded(): void {
    this.#analytics.loaded()
  }

  onDocumentError(): void {
    this.#analytics.failed()
  }

  onDocumentScroll(): void {
    if (this.#scrollActions().length) {
      this.#actionService.runActions(this.#scrollActions(), this.templateConfig())
    }
  }

  onDocumentScrollEnd(): void {
    if (this.#scrollEndActions().length) {
      this.#actionService.runActions(this.#scrollEndActions(), this.templateConfig())
    }
  }
}
